import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MediaUploadDialog } from '../../dialog/upload/component';
import { MediaService } from '../../media.service';

type MediaLibraryMode = 'picker' | 'manage';
type MediaLibrarySort = 'createdAt-dsc' | 'createdAt-asc' | 'id-asc' | 'id-dsc';

@Component({
  standalone: false,
  selector: 'media-library',
  templateUrl: './template.html',
  styleUrl: './style.scss',
})
export class MediaLibraryComponent implements OnInit, OnDestroy {
  @Input() mode: MediaLibraryMode = 'picker';
  @Output() imageChosen = new EventEmitter<string>();

  imageIds: string[] = [];

  readonly pageSize: number = 10;
  currentPageIndex: number = 0;
  canLoadMore: boolean = true;
  isLoadingMore: boolean = false;

  sort: MediaLibrarySort = 'createdAt-dsc';
  searchTerm: string = '';

  private activeSearchTerm: string = '';
  private requestVersion: number = 0;

  private destroy$ = new Subject<void>();
  private searchTerm$ = new Subject<string>();

  selected: string = '';

  constructor(
    private mediaService: MediaService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.searchTerm$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        void this.reloadFromFirstPage(value);
      });

    void this.reloadFromFirstPage('');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openAddMediaDialog() {
    const dialogRef = this.dialog.open(MediaUploadDialog, {
      width: '620px',
      maxWidth: '95vw',
    });

    dialogRef.afterClosed().subscribe((imageId: string | undefined) => {
      if (!imageId) {
        return;
      }

      this.imageUploaded(imageId);
    });
  }

  imageUploaded(imageId: string) {
    this.selected = imageId;
    this.imageIds.unshift(imageId);
  }

  select(image: string) {
    this.selected = image;
  }

  choose() {
    if (!this.selected) {
      return;
    }

    this.imageChosen.emit(this.selected);
  }

  get isPickerMode() {
    return this.mode === 'picker';
  }

  onSearchInput(value: string) {
    this.searchTerm = value;
    this.searchTerm$.next(value.trim().toLowerCase());
  }

  onSortChange() {
    void this.reloadFromFirstPage(this.activeSearchTerm);
  }

  async loadMore() {
    if (!this.canLoadMore || this.isLoadingMore) {
      return;
    }

    const currentRequestVersion = this.requestVersion;
    this.isLoadingMore = true;

    try {
      const nextPageIndex = this.currentPageIndex + 1;
      const page = await this.mediaService.listImageCodes({
        pagination: {
          pageIndex: nextPageIndex,
          pageSize: this.pageSize,
        },
        search: this.activeSearchTerm,
        sort: this.toApiSort(this.sort),
      });

      if (currentRequestVersion !== this.requestVersion) {
        return;
      }

      this.currentPageIndex = nextPageIndex;
      this.canLoadMore = page.length === this.pageSize;
      this.mergeImageIds(page);
    } finally {
      if (currentRequestVersion === this.requestVersion) {
        this.isLoadingMore = false;
      }
    }
  }

  private async reloadFromFirstPage(search: string) {
    this.requestVersion += 1;
    const currentRequestVersion = this.requestVersion;
    this.isLoadingMore = true;
    this.activeSearchTerm = search;

    try {
      const page = await this.mediaService.listImageCodes({
        pagination: {
          pageIndex: 0,
          pageSize: this.pageSize,
        },
        search: this.activeSearchTerm,
        sort: this.toApiSort(this.sort),
      });

      if (currentRequestVersion !== this.requestVersion) {
        return;
      }

      this.currentPageIndex = 0;
      this.canLoadMore = page.length === this.pageSize;
      this.imageIds = page;

      if (this.selected && !this.imageIds.includes(this.selected)) {
        this.selected = '';
      }
    } finally {
      if (currentRequestVersion === this.requestVersion) {
        this.isLoadingMore = false;
      }
    }
  }

  private mergeImageIds(newIds: string[]) {
    if (!newIds.length) {
      return;
    }

    const existing = new Set(this.imageIds);
    const uniqueNewIds = newIds.filter((id) => !existing.has(id));
    this.imageIds = [...this.imageIds, ...uniqueNewIds];
  }

  private toApiSort(sort: MediaLibrarySort): { active: 'id' | 'createdAt'; direction: 'ASC' | 'DSC' } {
    switch (sort) {
      case 'createdAt-asc':
        return { active: 'createdAt', direction: 'ASC' };
      case 'id-asc':
        return { active: 'id', direction: 'ASC' };
      case 'id-dsc':
        return { active: 'id', direction: 'DSC' };
      default:
        return { active: 'createdAt', direction: 'DSC' };
    }
  }
}
